import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import { type PianoKeyTone } from '@/components/piano/KeyboardPlate';
import { PianoKeyboard } from '@/components/piano/PianoKeyboard';
import { useElementWidth } from '@/hooks/use-element-width';
import { chartKeyboardLayout, PLOT_INSET } from '../chart-geometry';
import { ChartKeyTicks } from './ChartKeyTicks';
import type { Key } from '@/lib/piano/keyboard/key';
import { KeyColor } from '@/lib/piano/keyboard/key-color.enum';
import { octaveMarks } from '@/lib/piano/keyboard/keyboard-geometry';
export interface ChartKeyboardAxisProps {
  description: string;
  highlightedKey?: number | null;
  keys: readonly Key[];
  /**
   * The keys the figure is about — those the springs are on — their white
   * keys washed in brass. The black keys among them keep their colour, as in
   * the mockup: a run of washed white keys already takes them in.
   */
  markedKeys?: ReadonlySet<number>;
  /** Show every C, or every other one when the figure is narrow. */
  marks?: 'all' | 'sparse' | 'none';
  onKeyHover?: (keyNumber: number | null) => void;
}

/**
 * The keyboard standing in for the x axis.
 *
 * It is inset by exactly the margins of the plot above it, and the plot is
 * ruled by the balance rail, where every key gets the same slot. A reading
 * therefore sits above the back of its own key, and the wood of the key fans
 * out from there to the front — which is what a grand does, and why the
 * sticks are drawn here rather than a bare keyboard.
 */
export const ChartKeyboardAxis = t0 => {
  const $ = _c(33);
  const {
    description,
    highlightedKey,
    keys,
    markedKeys,
    marks: t1,
    onKeyHover
  } = t0;
  const marks = t1 === undefined ? "all" : t1;
  const [axis, width] = useElementWidth();
  let t2;
  if ($[0] !== keys) {
    t2 = keys.filter(_temp);
    $[0] = keys;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const whiteKeyCount = t2.length;
  let t3;
  if ($[2] !== whiteKeyCount || $[3] !== width) {
    t3 = chartKeyboardLayout(width, whiteKeyCount);
    $[2] = whiteKeyCount;
    $[3] = width;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const geometry = t3;
  let t4;
  if ($[5] !== keys || $[6] !== markedKeys) {
    t4 = markedKeys ? new Map(keys.filter(key_0 => key_0.color === KeyColor.White && markedKeys.has(key_0.number)).map(_temp2)) : undefined;
    $[5] = keys;
    $[6] = markedKeys;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const tones = t4;
  let t5;
  bb0: {
    if (marks === "none") {
      t5 = undefined;
      break bb0;
    }
    let t6;
    if ($[8] !== highlightedKey || $[9] !== keys || $[10] !== marks) {
      let t7;
      if ($[12] !== marks) {
        t7 = (mark, index) => marks === "all" || index % 2 === 0;
        $[12] = marks;
        $[13] = t7;
      } else {
        t7 = $[13];
      }
      let t8;
      if ($[14] !== highlightedKey) {
        t8 = mark_0 => ({
          at: mark_0.number,
          isStrong: mark_0.number === highlightedKey,
          text: mark_0.label
        });
        $[14] = highlightedKey;
        $[15] = t8;
      } else {
        t8 = $[15];
      }
      t6 = octaveMarks(keys).filter(t7).map(t8);
      $[8] = highlightedKey;
      $[9] = keys;
      $[10] = marks;
      $[11] = t6;
    } else {
      t6 = $[11];
    }
    t5 = t6;
  }
  const labels = t5;
  let t6;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      marginLeft: PLOT_INSET.left,
      marginRight: PLOT_INSET.right
    };
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== description || $[18] !== geometry || $[19] !== highlightedKey || $[20] !== keys || $[21] !== labels || $[22] !== onKeyHover || $[23] !== tones || $[24] !== width) {
    t7 = width > 0 ? <PianoKeyboard className="print:hidden" geometry={geometry} keys={keys} description={description} highlightedKey={highlightedKey} labels={labels} onKeyHover={onKeyHover} toneByKeyNumber={tones} /> : null;
    $[17] = description;
    $[18] = geometry;
    $[19] = highlightedKey;
    $[20] = keys;
    $[21] = labels;
    $[22] = onKeyHover;
    $[23] = tones;
    $[24] = width;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== keys || $[27] !== labels) {
    t8 = <ChartKeyTicks className="hidden print:block" keys={keys} labels={labels} />;
    $[26] = keys;
    $[27] = labels;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[29] !== axis || $[30] !== t7 || $[31] !== t8) {
    t9 = <div ref={axis} data-slot="chart-keyboard" style={t6}>{t7}{t8}</div>;
    $[29] = axis;
    $[30] = t7;
    $[31] = t8;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  return t9;
};
function _temp(key) {
  return key.color === KeyColor.White;
}
function _temp2(key_1) {
  return [key_1.number, "marked"];
}